// Lightweight hook around Web Speech API for speech-to-text
// Usage:
// const { supported, listening, transcript, start, stop, error } = useSpeechRecognition({ lang: 'en-US', interim: true })

import { useCallback, useEffect, useRef, useState } from 'react';

export default function useSpeechRecognition({ lang = 'en-US', interim = true, continuous = false } = {}) {
  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!SR;
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supported) return;
    const rec = new SR();
    recRef.current = rec;
    rec.continuous = !!continuous; 
    rec.interimResults = !!interim;
    rec.lang = lang;

    rec.onresult = (e) => {
      let text = '';
      // Process from the beginning of the results list to get full accumulated transcript
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i][0].transcript;
      }
      setTranscript(text);
    };

    rec.onstart = () => {
      setListening(true);
    };

    rec.onend = () => {
      setListening(false);
    };

    rec.onerror = (e) => {
      setError(e);
      setListening(false);
    };

    return () => {
      try { rec.stop(); } catch {}
      recRef.current = null;
    };
  }, [supported, lang, interim, continuous]);

  const start = useCallback(() => {
    if (!supported || !recRef.current) return;
    setTranscript('');
    setError(null);
    try {
      recRef.current.start();
    } catch (e) {
      setError(e);
    }
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported || !recRef.current) return;
    try { recRef.current.stop(); } catch {}
  }, [supported]);

  return { supported, listening, transcript, start, stop, error };
}